// Setup › Structure › Assessments (`/c/{slug}/setup/assessments`; M8-ASSESS, managers): rounds of
// the team X-Ray. The list shows every round (state, questionnaire, who answered, the report's
// version) with "New round"; a round (`?round=`) opens on three views (`?view=`):
// - Progress: who answered, who is half-way, who has not opened their link, the reminders sent
//   (Remind is off with the reason when it may not go: answered, twice already, 20 h apart);
//   the links minted by opening or adding people are shown once, with Copy (no email is sent);
//   Open (a draft), Close (computes the report), Add people, Remove, Delete a draft;
// - Report: the team's numbers, gaps and recommended actions, the role cards (report-view.tsx);
// - Proposed chart: seats, responsibilities and KPIs the report suggests, accepted or rejected
//   one by one, reviewed as a diff and applied on confirmation (report-view.tsx).
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Progress,
  StatGrid,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  ViewTabs,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeftIcon,
  BellRingIcon,
  ClipboardCheckIcon,
  CopyIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  UserPlusIcon,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { ConfirmRowAction } from "../confirm-row-action";
import { formatDateTime } from "../format";
import { Loading } from "../screen-parts";
import { ROW_HOVER, RowAction, RowActions } from "../settings/row-actions";
import { SetupPage } from "../settings/setup-page";
import { ConfirmButton, ErrorNotice, FormError } from "../structure/parts";
import {
  PARTICIPANT_STATE,
  type Participant,
  progressLine,
  progressPercent,
  QUESTIONNAIRE_LABEL,
  ROUND_STATE,
  type RoundDetail,
  remindBlocked,
} from "./assess-model";
import { assessKeys, assessPeopleQuery, roundQuery, roundsQuery } from "./assess-queries";
import { ProposedChart, ReportPanel } from "./report-view";

export type AssessView = "progress" | "report" | "chart";

type Link = RoundDetail["links"][number];

export function AssessmentsScreen({
  api,
  company,
  roundId,
  view,
  onRound,
  onView,
}: {
  api: Api;
  company: CompanyAccess;
  roundId: string | undefined;
  view: AssessView;
  onRound: (id: string | undefined, view?: AssessView) => void;
  onView: (view: AssessView) => void;
}) {
  if (roundId)
    return (
      <RoundPage
        api={api}
        company={company}
        roundId={roundId}
        view={view}
        onBack={() => onRound(undefined)}
        onView={onView}
      />
    );
  return <RoundList api={api} company={company} onRound={onRound} />;
}

// The list ------------------------------------------------------------------------------------

function RoundList({
  api,
  company,
  onRound,
}: {
  api: Api;
  company: CompanyAccess;
  onRound: (id: string | undefined, view?: AssessView) => void;
}) {
  const rounds = useQuery(roundsQuery(api, company.id));
  const [creating, setCreating] = useState(false);
  const list = rounds.data?.data ?? [];
  const [editing, setEditing] = useState<(typeof list)[number] | null>(null);
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (id: string) =>
      api.client.assessments.deleteRound({ params: { co: company.id, id } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: assessKeys.rounds(company.id) }),
  });
  return (
    <SetupPage
      title="Assessments"
      icon={ClipboardCheckIcon}
      slot="assessments"
      {...(rounds.data ? { count: list.length } : {})}
      actions={
        <Button size="sm" onClick={() => setCreating(true)} disabled={!rounds.data}>
          <PlusIcon aria-hidden={true} />
          New round
        </Button>
      }
    >
      {rounds.isPending ? (
        <Loading label="Loading assessment rounds" rows={4} />
      ) : rounds.isError ? (
        <ErrorNotice what="Assessment rounds" error={rounds.error} />
      ) : list.length === 0 ? (
        <EmptyState
          variant="card"
          icon={ClipboardCheckIcon}
          title="No assessment rounds yet"
          description="A round asks your team about their tasks, hours, energy and role clarity, then suggests seats, responsibilities and KPIs for the chart."
          actions={<Button onClick={() => setCreating(true)}>New round</Button>}
        />
      ) : (
        <Table data-slot="assessment-rounds">
          <TableCaption className="sr-only">Assessment rounds of {company.name}</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Round</TableHead>
              <TableHead scope="col">State</TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Responded
              </TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Report
              </TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((r) => (
              <TableRow key={r.id} className="group/row" data-round={r.id}>
                <TableHead scope="row" className="min-w-0">
                  <button
                    type="button"
                    className="text-left font-medium text-foreground underline-offset-4 hover:underline"
                    onClick={() => onRound(r.id, r.report_version ? "report" : "progress")}
                  >
                    {r.name}
                  </button>
                  <span className="block truncate text-xs font-normal text-muted-foreground">
                    #{r.number} · {QUESTIONNAIRE_LABEL[r.questionnaire]}
                    <span className="md:hidden"> · {progressLine(r)}</span>
                  </span>
                </TableHead>
                <TableCell>
                  <Badge variant={ROUND_STATE[r.state].tone} dot>
                    {ROUND_STATE[r.state].label}
                  </Badge>
                </TableCell>
                <TableCell className="max-md:hidden">
                  <div className="flex min-w-[140px] items-center gap-2">
                    <Progress value={progressPercent(r)} className="w-20" aria-label="Responded" />
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {progressLine(r)}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="max-md:hidden">
                  {r.report_version ? (
                    <button
                      type="button"
                      className="text-sm text-primary-text underline-offset-4 hover:underline"
                      onClick={() => onRound(r.id, "report")}
                    >
                      Version {r.report_version}
                    </button>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="w-20">
                  {r.state === "draft" || r.state === "open" ? (
                    <RowActions>
                      <RowAction
                        icon={PencilIcon}
                        label={`Edit ${r.name}`}
                        tip="Edit"
                        onClick={() => setEditing(r)}
                      />
                      {r.state === "draft" ? (
                        <ConfirmRowAction
                          icon={Trash2Icon}
                          label={`Delete ${r.name}`}
                          tip="Delete draft"
                          disabled={remove.isPending}
                          title={`Delete "${r.name}"?`}
                          description="The draft and the people asked are removed."
                          confirmLabel="Delete draft"
                          onConfirm={() => remove.mutate(r.id)}
                        />
                      ) : null}
                    </RowActions>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <FormError what="Deleting the round" error={remove.error} />
      {editing ? (
        <EditRoundDialog
          api={api}
          company={company}
          round={editing}
          onClose={() => setEditing(null)}
          onSaved={(next) => {
            qc.setQueryData(assessKeys.round(company.id, next.id), next);
            void qc.invalidateQueries({ queryKey: assessKeys.rounds(company.id) });
            setEditing(null);
          }}
        />
      ) : null}
      {creating && rounds.data ? (
        <NewRoundDialog
          api={api}
          company={company}
          questionnaires={rounds.data.questionnaires}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            onRound(id, "progress");
          }}
        />
      ) : null}
    </SetupPage>
  );
}

// People picker (new round, add people) -------------------------------------------------------

type GuestDraft = { name: string; email: string; role_title: string; reports_to_seat_id: string };
const blankGuest = (): GuestDraft => ({
  name: "",
  email: "",
  role_title: "",
  reports_to_seat_id: "",
});

function usePeopleDraft() {
  const [members, setMembers] = useState<string[]>([]);
  const [guests, setGuests] = useState<GuestDraft[]>([]);
  const body = {
    member_ids: members,
    guests: guests
      .filter((g) => g.name.trim() && g.email.trim())
      .map((g) => ({
        name: g.name.trim(),
        email: g.email.trim().toLowerCase(),
        role_title: g.role_title.trim() || null,
        reports_to_seat_id: g.reports_to_seat_id || null,
      })),
  };
  return { members, setMembers, guests, setGuests, body };
}

function PeoplePicker({
  api,
  company,
  draft,
  taken,
}: {
  api: Api;
  company: CompanyAccess;
  draft: ReturnType<typeof usePeopleDraft>;
  /** Members already asked (not offered again). */
  taken: ReadonlySet<string>;
}) {
  const people = useQuery(assessPeopleQuery(api, company.id));
  const seats = useQuery({
    queryKey: ["company", company.id, "seats", "assess-picker"],
    queryFn: async () =>
      (await api.client.seats.list({ params: { co: company.id }, query: { limit: 200 } })).data,
  });
  const offered = (people.data ?? []).filter((p) => !taken.has(p.id));
  return (
    <div className="flex flex-col gap-4">
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium text-foreground">Members</legend>
        {people.isPending ? (
          <Loading label="Loading people" rows={2} />
        ) : offered.length === 0 ? (
          <p className="text-sm text-muted-foreground">Everyone in the company is asked already.</p>
        ) : (
          <div className="flex max-h-44 flex-col gap-1 overflow-y-auto rounded-md border border-border p-2">
            {offered.map((p) => {
              const id = `assess-member-${p.id}`;
              return (
                <label key={p.id} htmlFor={id} className="flex items-center gap-2 py-1 text-sm">
                  <Checkbox
                    id={id}
                    checked={draft.members.includes(p.id)}
                    onCheckedChange={(v) =>
                      draft.setMembers((m) =>
                        v === true ? [...m, p.id] : m.filter((x) => x !== p.id),
                      )
                    }
                  />
                  <span className="min-w-0 truncate">{p.display_name}</span>
                </label>
              );
            })}
          </div>
        )}
      </fieldset>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium text-foreground">
          Guests{" "}
          <span className="font-normal text-muted-foreground">(no account; a link only)</span>
        </legend>
        {draft.guests.map((g, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: rows are edited in place by position
            key={i}
            className="grid grid-cols-2 gap-2 rounded-md border border-border p-2 max-md:grid-cols-1"
            data-slot="guest-row"
          >
            <Input
              aria-label="Guest name"
              placeholder="Name"
              value={g.name}
              maxLength={200}
              onChange={(e) =>
                draft.setGuests((x) =>
                  x.map((y, j) => (j === i ? { ...y, name: e.target.value } : y)),
                )
              }
            />
            <Input
              aria-label="Guest email"
              placeholder="Email"
              type="email"
              value={g.email}
              onChange={(e) =>
                draft.setGuests((x) =>
                  x.map((y, j) => (j === i ? { ...y, email: e.target.value } : y)),
                )
              }
            />
            <Input
              aria-label="Guest role"
              placeholder="Role (optional)"
              value={g.role_title}
              maxLength={120}
              onChange={(e) =>
                draft.setGuests((x) =>
                  x.map((y, j) => (j === i ? { ...y, role_title: e.target.value } : y)),
                )
              }
            />
            <div className="flex items-center gap-2">
              <NativeSelect
                aria-label="Reports to"
                className="min-w-0 flex-1"
                value={g.reports_to_seat_id}
                onChange={(e) =>
                  draft.setGuests((x) =>
                    x.map((y, j) => (j === i ? { ...y, reports_to_seat_id: e.target.value } : y)),
                  )
                }
              >
                <NativeSelectOption value="">Reports to…</NativeSelectOption>
                {(seats.data ?? []).map((s) => (
                  <NativeSelectOption key={s.id} value={s.id}>
                    {s.title}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove guest ${g.name || i + 1}`}
                onClick={() => draft.setGuests((x) => x.filter((_, j) => j !== i))}
              >
                <Trash2Icon aria-hidden={true} />
              </Button>
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="self-start"
          onClick={() => draft.setGuests((x) => [...x, blankGuest()])}
        >
          <UserPlusIcon aria-hidden={true} />
          Add a guest
        </Button>
      </fieldset>
    </div>
  );
}

function NewRoundDialog({
  api,
  company,
  questionnaires,
  onClose,
  onCreated,
}: {
  api: Api;
  company: CompanyAccess;
  questionnaires: { key: "full" | "delta"; label: string; description: string; minutes: number }[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [questionnaire, setQuestionnaire] = useState<"full" | "delta">("full");
  const [name, setName] = useState("");
  const [days, setDays] = useState("7");
  const [openNow, setOpenNow] = useState(false);
  const people = usePeopleDraft();
  const create = useMutation({
    mutationFn: () =>
      api.client.assessments.createRound({
        params: { co: company.id },
        body: {
          questionnaire,
          ...(name.trim() ? { name: name.trim() } : {}),
          invite_expiry_days: Math.min(60, Math.max(1, Number(days) || 7)),
          ...people.body,
          open: openNow,
        },
      }),
    onSuccess: (r) => {
      qc.setQueryData(assessKeys.round(company.id, r.id), r);
      void qc.invalidateQueries({ queryKey: assessKeys.rounds(company.id) });
      onCreated(r.id);
    },
  });
  const asked = people.members.length + people.body.guests.length;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-xl" data-slot="new-round">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>New assessment round</DialogTitle>
            <DialogDescription>
              Pick the questionnaire and who answers it. Each person gets a private link that
              expires; nothing is emailed.
            </DialogDescription>
          </DialogHeader>
          <fieldset className="grid grid-cols-2 gap-2 max-md:grid-cols-1">
            <legend className="sr-only">Questionnaire</legend>
            {questionnaires.map((q) => (
              <label
                key={q.key}
                className="flex cursor-pointer flex-col gap-1 rounded-md border border-border p-3 text-sm has-checked:border-primary has-checked:bg-primary-soft"
              >
                <span className="flex items-center gap-2 font-medium text-foreground">
                  <input
                    type="radio"
                    name="questionnaire"
                    value={q.key}
                    checked={questionnaire === q.key}
                    onChange={() => setQuestionnaire(q.key)}
                    className="accent-(--primary)"
                  />
                  {q.label}
                  <span className="ml-auto text-xs font-normal text-muted-foreground">
                    ~{q.minutes} min
                  </span>
                </span>
                <span className="text-xs text-muted-foreground">{q.description}</span>
              </label>
            ))}
          </fieldset>
          <div className="grid grid-cols-[1fr_140px] gap-3 max-md:grid-cols-1">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="round-name">Name</Label>
              <Input
                id="round-name"
                placeholder={questionnaire === "full" ? "Team X-Ray" : "Quick scan"}
                value={name}
                maxLength={200}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="round-days">Links last (days)</Label>
              <Input
                id="round-days"
                type="number"
                min={1}
                max={60}
                value={days}
                onChange={(e) => setDays(e.target.value)}
              />
            </div>
          </div>
          <PeoplePicker api={api} company={company} draft={people} taken={new Set()} />
          <label htmlFor="round-open" className="flex items-center gap-2 text-sm">
            <Checkbox
              id="round-open"
              checked={openNow}
              onCheckedChange={(v) => setOpenNow(v === true)}
            />
            Open now and show the links
          </label>
          <FormError what="Creating the round" error={create.error} />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || (openNow && asked === 0)}>
              {create.isPending
                ? "Creating…"
                : openNow
                  ? `Create and open (${asked})`
                  : "Create draft"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// A round ------------------------------------------------------------------------------------

function RoundPage({
  api,
  company,
  roundId,
  view,
  onBack,
  onView,
}: {
  api: Api;
  company: CompanyAccess;
  roundId: string;
  view: AssessView;
  onBack: () => void;
  onView: (view: AssessView) => void;
}) {
  const round = useQuery(roundQuery(api, company.id, roundId));
  const r = round.data;
  const reported = !!r?.report_version;
  const current: AssessView = reported ? view : "progress";
  return (
    <SetupPage
      crumbs={[
        "Setup",
        { label: "Assessments", render: <button type="button" onClick={onBack} /> },
      ]}
      title={r?.name ?? "Round"}
      icon={ClipboardCheckIcon}
      slot="assessment-round"
      {...(r
        ? {
            badge: (
              <Badge variant={ROUND_STATE[r.state].tone} dot>
                {ROUND_STATE[r.state].label}
              </Badge>
            ),
          }
        : {})}
      actions={
        <Button size="sm" variant="ghost" onClick={onBack}>
          <ArrowLeftIcon aria-hidden={true} />
          All rounds
        </Button>
      }
      toolbar={
        r ? (
          <div className="border-b border-border px-6 max-md:px-4">
            <ViewTabs
              aria-label="Round views"
              value={current}
              onValueChange={(v) => onView(v as AssessView)}
              tabs={[
                { value: "progress", label: "Progress", count: r.respondents },
                ...(reported
                  ? [
                      { value: "report", label: "Report" },
                      { value: "chart", label: "Proposed chart" },
                    ]
                  : []),
              ]}
            />
          </div>
        ) : undefined
      }
      width="doc"
    >
      {round.isPending ? (
        <Loading label="Loading the round" rows={5} />
      ) : round.isError || !r ? (
        <ErrorNotice what="The round" error={round.error} />
      ) : current === "report" ? (
        <ReportPanel api={api} company={company} round={r} />
      ) : current === "chart" ? (
        <ProposedChart api={api} company={company} round={r} />
      ) : (
        <ProgressPanel
          api={api}
          company={company}
          round={r}
          onDeleted={onBack}
          onReport={() => onView("report")}
        />
      )}
    </SetupPage>
  );
}

function copy(text: string, what: string) {
  const full = `${window.location.origin}${text}`;
  void navigator.clipboard?.writeText(full).then(
    () => toast.add({ title: `${what} copied` }),
    () => toast.add({ title: "The link could not be copied", type: "warning" }),
  );
}

function ProgressPanel({
  api,
  company,
  round: r,
  onDeleted,
  onReport,
}: {
  api: Api;
  company: CompanyAccess;
  round: RoundDetail;
  onDeleted: () => void;
  onReport: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const [links, setLinks] = useState<Link[]>(r.links);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const fresh = (next: RoundDetail) => {
    qc.setQueryData(assessKeys.round(co, next.id), next);
    void qc.invalidateQueries({ queryKey: assessKeys.rounds(co) });
    if (next.links.length)
      setLinks((l) => [
        ...next.links,
        ...l.filter((x) => !next.links.some((y) => y.participant_id === x.participant_id)),
      ]);
  };
  const open = useMutation({
    mutationFn: () => api.client.assessments.openRound({ params: { co, id: r.id } }),
    onSuccess: fresh,
  });
  const close = useMutation({
    mutationFn: () => api.client.assessments.closeRound({ params: { co, id: r.id } }),
    onSuccess: (next) => {
      fresh(next);
      void qc.invalidateQueries({ queryKey: assessKeys.all(co) });
      onReport();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.client.assessments.deleteRound({ params: { co, id: r.id } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assessKeys.rounds(co) });
      onDeleted();
    },
  });
  const remind = useMutation({
    mutationFn: (p: Participant) =>
      api.client.assessments.remind({ params: { co, id: r.id, participant_id: p.id } }),
    onSuccess: (res) => {
      setLinks((l) => [res.link, ...l.filter((x) => x.participant_id !== res.link.participant_id)]);
      void qc.invalidateQueries({ queryKey: assessKeys.round(co, r.id) });
      toast.add({
        title: `New link for ${res.link.display_name}`,
        description: "Copy it below and send it.",
      });
    },
  });
  const removeP = useMutation({
    mutationFn: (p: Participant) =>
      api.client.assessments.removeParticipant({ params: { co, id: r.id, participant_id: p.id } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: assessKeys.all(co) }),
  });
  const p = r.progress;
  const live = r.state === "draft" || r.state === "open";
  return (
    <div className="flex flex-col gap-4" data-slot="round-progress">
      <p className="text-sm text-muted-foreground">
        {r.questionnaire_info.label} · about {r.questionnaire_info.minutes} minutes · links last{" "}
        {r.invite_expiry_days} days
        {r.opened_at ? ` · opened ${formatDateTime(r.opened_at)}` : ""}
      </p>
      <StatGrid dense>
        <StatTile size="sm" label="Responded" value={`${p.completed} / ${r.respondents}`} />
        <StatTile size="sm" label="In progress" value={p.in_progress} />
        <StatTile size="sm" label="Not started" value={p.invited + p.opened} />
        <StatTile size="sm" label="Didn't answer" value={p.expired} />
      </StatGrid>
      <div className="flex flex-wrap items-center gap-2">
        {live ? (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            <UserPlusIcon aria-hidden={true} />
            Add people
          </Button>
        ) : null}
        {live ? (
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            <PencilIcon aria-hidden={true} />
            Edit round
          </Button>
        ) : null}
        {r.state === "draft" ? (
          <>
            <ConfirmButton
              label="Open round"
              variant="secondary"
              destructive={false}
              title={`Open "${r.name}"?`}
              description={`Each of the ${r.respondents} people gets a private link that lasts ${r.invite_expiry_days} days. You copy and send the links; nothing is emailed.`}
              confirmLabel="Open and show links"
              disabled={r.respondents === 0}
              pending={open.isPending}
              onConfirm={() => open.mutateAsync()}
            />
            <ConfirmButton
              label="Delete draft"
              variant="ghost"
              title={`Delete "${r.name}"?`}
              description="The draft and the people asked are removed."
              confirmLabel="Delete draft"
              pending={remove.isPending}
              onConfirm={() => remove.mutateAsync()}
            />
          </>
        ) : null}
        {r.state === "open" ? (
          <ConfirmButton
            label="Close and report"
            variant="secondary"
            destructive={false}
            title={`Close "${r.name}"?`}
            description={`${p.completed} of ${r.respondents} answered. Links stop working, the report is computed and the proposed chart is drafted.`}
            confirmLabel="Close round"
            pending={close.isPending}
            onConfirm={() => close.mutateAsync()}
          />
        ) : null}
      </div>
      <FormError what="Opening the round" error={open.error} />
      <FormError what="Closing the round" error={close.error} />
      <FormError what="The reminder" error={remind.error} />
      <FormError what="Removing" error={removeP.error ?? remove.error} />
      {links.length > 0 ? (
        <Notice tone="info" title="Links to send (shown once)" data-slot="round-links">
          <ul className="flex flex-col gap-1.5">
            {links.map((l) => (
              <li key={l.participant_id} className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="font-medium text-foreground">{l.display_name}</span>
                <code className="min-w-0 max-w-full flex-1 truncate font-mono text-xs text-muted-foreground">
                  {l.path}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copy(l.path, `${l.display_name}'s link`)}
                  aria-label={`Copy ${l.display_name}'s link`}
                >
                  <CopyIcon aria-hidden={true} />
                  Copy
                </Button>
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}
      {r.participants.length === 0 ? (
        <EmptyState
          variant="card"
          icon={UserPlusIcon}
          title="No one asked yet"
          description="Add members or guests, then open the round."
        />
      ) : (
        <Table data-slot="round-participants">
          <TableCaption className="sr-only">Respondents of {r.name}</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Respondent</TableHead>
              <TableHead scope="col">Status</TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Reminders
              </TableHead>
              <TableHead scope="col" className="text-right">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {r.participants.map((x) => {
              const blocked = remindBlocked(r, x);
              const st = PARTICIPANT_STATE[x.state];
              return (
                <TableRow
                  key={x.id}
                  data-participant={x.id}
                  data-state={x.state}
                  className="group/row"
                >
                  <TableHead scope="row" className="min-w-0">
                    <span className="flex items-center gap-2 font-medium text-foreground">
                      <span className="truncate">{x.display_name}</span>
                      {x.kind === "guest" ? <Badge variant="outline">Guest</Badge> : null}
                      {x.is_owner ? <Badge variant="accent">Owner</Badge> : null}
                    </span>
                    <span className="block truncate text-xs font-normal text-muted-foreground">
                      {x.seat_title ?? x.role_title ?? x.email ?? "No seat"}
                    </span>
                  </TableHead>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge variant={st.tone} dot>
                        {st.label}
                      </Badge>
                      {x.state === "in_progress" || x.state === "opened" ? (
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {Math.round(x.answered_share * 100)}% answered
                        </span>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-md:hidden">
                    {x.reminder_count
                      ? `${x.reminder_count} · last ${formatDateTime(x.last_reminded_at ?? "")}`
                      : "None"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {r.state === "open" && x.state !== "completed" ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!!blocked || remind.isPending}
                          title={blocked ?? "Send a fresh link"}
                          aria-label={`Remind ${x.display_name}${blocked ? ` (${blocked})` : ""}`}
                          onClick={() => remind.mutate(x)}
                        >
                          <BellRingIcon aria-hidden={true} />
                          <span className="max-md:hidden">Remind</span>
                        </Button>
                      ) : null}
                      {live && x.state !== "completed" ? (
                        <ConfirmRowAction
                          icon={Trash2Icon}
                          label={`Remove ${x.display_name}`}
                          tip="Stop asking"
                          className={ROW_HOVER}
                          disabled={removeP.isPending}
                          title={`Stop asking ${x.display_name}?`}
                          description={
                            x.state === "invited"
                              ? "They come off this round."
                              : "They come off this round; their link stops working and what they answered so far is dropped."
                          }
                          confirmLabel="Remove"
                          onConfirm={() => removeP.mutate(x)}
                        />
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {adding ? (
        <AddPeopleDialog
          api={api}
          company={company}
          round={r}
          onClose={() => setAdding(false)}
          onAdded={(next) => {
            fresh(next);
            setAdding(false);
          }}
        />
      ) : null}
      {editing ? (
        <EditRoundDialog
          api={api}
          company={company}
          round={r}
          onClose={() => setEditing(false)}
          onSaved={(next) => {
            fresh(next);
            setEditing(false);
          }}
        />
      ) : null}
    </div>
  );
}

/** Rename a draft or open round, or change how long its links last (PATCH, draft or open). */
function EditRoundDialog({
  api,
  company,
  round: r,
  onClose,
  onSaved,
}: {
  api: Api;
  company: CompanyAccess;
  round: { id: string; name: string; invite_expiry_days: number };
  onClose: () => void;
  onSaved: (next: RoundDetail) => void;
}) {
  const [name, setName] = useState(r.name);
  const [days, setDays] = useState(String(r.invite_expiry_days));
  const n = Number(days);
  const ok = name.trim().length > 0 && Number.isInteger(n) && n >= 1 && n <= 60;
  const save = useMutation({
    mutationFn: () =>
      api.client.assessments.updateRound({
        params: { co: company.id, id: r.id },
        body: { name: name.trim(), invite_expiry_days: n },
      }),
    onSuccess: onSaved,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ok && !save.isPending) save.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Edit "{r.name}"</DialogTitle>
            <DialogDescription>Links already sent keep the length they had.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-[1fr_140px] gap-3 max-md:grid-cols-1">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-round-name">Name</Label>
              <Input
                id="edit-round-name"
                value={name}
                maxLength={200}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-round-days">Links last (days)</Label>
              <Input
                id="edit-round-days"
                type="number"
                min={1}
                max={60}
                value={days}
                onChange={(e) => setDays(e.target.value)}
              />
            </div>
          </div>
          <FormError what="Saving the round" error={save.error} />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!ok || save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddPeopleDialog({
  api,
  company,
  round: r,
  onClose,
  onAdded,
}: {
  api: Api;
  company: CompanyAccess;
  round: RoundDetail;
  onClose: () => void;
  onAdded: (next: RoundDetail) => void;
}) {
  const people = usePeopleDraft();
  const add = useMutation({
    mutationFn: () =>
      api.client.assessments.addParticipants({
        params: { co: company.id, id: r.id },
        body: people.body,
      }),
    onSuccess: onAdded,
  });
  const taken = new Set(r.participants.flatMap((p) => (p.membership_id ? [p.membership_id] : [])));
  const n = people.members.length + people.body.guests.length;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add people to "{r.name}"</DialogTitle>
          <DialogDescription>
            {r.state === "open"
              ? "The round is open: their links are shown once you add them."
              : "Their links are minted when the round opens."}
          </DialogDescription>
        </DialogHeader>
        <PeoplePicker api={api} company={company} draft={people} taken={taken} />
        <FormError what="Adding people" error={add.error} />
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={n === 0 || add.isPending} onClick={() => add.mutate()}>
            {add.isPending ? "Adding…" : `Add ${n}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
